import React, { useState, useEffect } from 'react'
import { message, Pagination, Select, Modal } from 'antd'
import { getContents, deleteContent, createContent, updateContent } from '../../../Web Apis/webApis'
import Load from '../../Load/Load'
import { AiFillDelete, AiFillEdit, AiOutlineEye } from "react-icons/ai";
import { FaSearch, FaPlus } from 'react-icons/fa';
import { MdPolicy } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;
const { confirm } = Modal;

const AdminContent = () => {
    const navigate = useNavigate()
    const [contents, setContents] = useState([])
    const [loading, setLoading] = useState(false)
    const [showEditContent, setShowEditContent] = useState(false)
    const [showViewContent, setShowViewContent] = useState(false)
    const [selectedContent, setSelectedContent] = useState({})
    const [action, setAction] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage] = useState(10)
    const [searchQuery, setSearchQuery] = useState("")
    const [typeFilter, setTypeFilter] = useState("")
    const [publishedFilter, setPublishedFilter] = useState("")

    // Form states
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        type: 'other',
        isPublished: true
    })

    const handlePageChange = (page) => {
        setCurrentPage(page)
    }

    // Calculate the current items for the page
    const indexOfLastItem = currentPage * itemsPerPage
    const indexOfFirstItem = indexOfLastItem - itemsPerPage

    const filteredContents = contents.filter((content) => {
        const query = searchQuery.toLowerCase()
        const matchesSearch = (
            content.title?.toLowerCase().includes(query) ||
            content.content?.toLowerCase().includes(query) ||
            content.type?.toLowerCase().includes(query)
        )
        const matchesType = !typeFilter || content.type === typeFilter
        const matchesPublished = publishedFilter === "" || content.isPublished === (publishedFilter === "true")

        return matchesSearch && matchesType && matchesPublished
    })

    const currentContents = filteredContents.slice(indexOfFirstItem, indexOfLastItem)

    const fetchContents = async () => {
        try {
            setLoading(true)
            const response = await getContents(true) // Include unpublished content
            if (response?.data?.success) {
                setContents(response?.data?.data || [])
            }
        } catch (error) {
            console.log("Error fetching contents", error.message)
            if (error.message === 'Network Error' || error.message.includes('ERR_INTERNET_DISCONNECTED')) {
                message.error("Network Error")
                return
            }
            if (error?.response?.status === 401) {
                window.alert("Your account is logged in to another device, please login again!")
                localStorage.removeItem('token')
                window.location.href = "/login"
            }
            if (error?.response?.status === 500) {
                message.error("Server not responding")
            } else {
                message.error("Error fetching contents")
            }
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchContents()
    }, [action])

    const handleDeleteContent = async (content) => {
        confirm({
            title: 'Are you sure you want to delete this content?',
            content: `Content: ${content.title}`,
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                try {
                    setLoading(true)
                    const response = await deleteContent(content._id)
                    if (response?.data?.success) {
                        message.success(response?.data?.message || "Content deleted successfully")
                        setAction(!action)
                    }
                } catch (error) {
                    console.log("Error deleting content", error.message)
                    message.error(error?.response?.data?.message || "Error deleting content")
                } finally {
                    setLoading(false)
                }
            }
        })
    }

    const handleTogglePublished = async (content, checked) => {
        try {
            setLoading(true)
            const response = await updateContent(content._id, { isPublished: checked })
            if (response?.data?.success) {
                message.success(`Content ${checked ? 'published' : 'unpublished'} successfully`)
                setAction(!action)
            }
        } catch (error) {
            console.log("Error updating content status", error.message)
            message.error(error?.response?.data?.message || "Error updating content status")
        } finally {
            setLoading(false)
        }
    }

    const handleViewContent = (content) => {
        setSelectedContent(content)
        setShowViewContent(true)
    }

    const handleEditContent = (content) => {
        setSelectedContent(content)
        setFormData({
            title: content.title,
            content: content.content,
            type: content.type,
            isPublished: content.isPublished
        })
        setShowEditContent(true)
    }


    const handleFormSubmit = async (isEdit = false) => {
        try {
            setLoading(true)
            let response

            if (isEdit) {
                response = await updateContent(selectedContent._id, formData)
            } else {
                response = await createContent(formData)
            }

            if (response?.data?.success) {
                message.success(response?.data?.message || `Content ${isEdit ? 'updated' : 'created'} successfully`)
                setAction(!action)
                setShowEditContent(false)
            }
        } catch (error) {
            console.log(`Error ${isEdit ? 'updating' : 'creating'} content`, error.message)
            message.error(error?.response?.data?.message || `Error ${isEdit ? 'updating' : 'creating'} content`)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="p-6 mt-16">
            {/* Header Section with Add Button */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">Content Management</h2>
                    <p className="text-gray-600 mt-1">Manage website content, policies, and information</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => navigate('/admin/content/add')}
                        className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center gap-3 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    >
                        <FaPlus className="text-lg" />
                        <span className="font-medium">Add New Content</span>
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-blue-100 text-sm">Total Content</p>
                            <p className="text-2xl font-bold">{contents.length}</p>
                        </div>
                        <div className="text-blue-200 text-2xl">
                            <FaPlus />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-green-100 text-sm">Published</p>
                            <p className="text-2xl font-bold">{contents.filter(c => c.isPublished).length}</p>
                        </div>
                        <div className="text-green-200 text-2xl">
                            <AiOutlineEye />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-purple-100 text-sm">Privacy Policy</p>
                            <p className="text-2xl font-bold">{contents.filter(c => c.type === 'privacy-policy').length}</p>
                        </div>
                        <div className="text-purple-200 text-2xl">
                            <MdPolicy />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-orange-100 text-sm">FAQ Items</p>
                            <p className="text-2xl font-bold">{contents.filter(c => c.type === 'faq').length}</p>
                        </div>
                        <div className="text-orange-200 text-2xl">
                            <AiFillEdit />
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                        <div className="relative">
                            <FaSearch className="absolute left-3 top-3 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search contents..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                        <Select
                            value={typeFilter}
                            onChange={setTypeFilter}
                            className="w-full"
                            allowClear
                        >
                            <Option value="">All Types</Option>
                            <Option value="privacy-policy">Privacy Policy</Option>
                            <Option value="terms-conditions">Terms & Conditions</Option>
                            <Option value="about-us">About Us</Option>
                            <Option value="faq">FAQ</Option>
                            <Option value="other">Other</Option>
                        </Select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <Select
                            value={publishedFilter}
                            onChange={setPublishedFilter}
                            className="w-full"
                            allowClear
                        >
                            <Option value="">All Status</Option>
                            <Option value="true">Published</Option>
                            <Option value="false">Draft</Option>
                        </Select>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => {
                                setSearchQuery("")
                                setTypeFilter("")
                                setPublishedFilter("")
                            }}
                            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition duration-200 w-full"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Enhanced Content Table */}
            <div className="bg-white shadow-xl rounded-xl overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Content Details</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Last Updated</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {currentContents?.length > 0 ? (
                                currentContents?.map((content, index) => (
                                    <tr key={content._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {(currentPage - 1) * itemsPerPage + index + 1}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900 max-w-xs truncate" title={content.title}>
                                                {content.title}
                                            </div>
                                            <div className="text-sm text-gray-500 max-w-xs truncate" title={content.content}>
                                                {content.content?.substring(0, 50)}...
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                                content.type === 'privacy-policy' ? 'bg-blue-100 text-blue-800' :
                                                content.type === 'terms-conditions' ? 'bg-green-100 text-green-800' :
                                                content.type === 'about-us' ? 'bg-purple-100 text-purple-800' :
                                                content.type === 'faq' ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-gray-100 text-gray-800'
                                            }`}>
                                                {content.type}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                                content.isPublished ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                            }`}>
                                                {content.isPublished ? 'Published' : 'Draft'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(content.updatedAt).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => handleViewContent(content)}
                                                    className="text-blue-600 hover:text-blue-900 p-1"
                                                    title="View"
                                                >
                                                    <AiOutlineEye className="text-lg" />
                                                </button>
                                                <button
                                                    onClick={() => handleEditContent(content)}
                                                    className="text-green-600 hover:text-green-900 p-1"
                                                    title="Edit"
                                                >
                                                    <AiFillEdit className="text-lg" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteContent(content)}
                                                    className="text-red-600 hover:text-red-900 p-1"
                                                    title="Delete"
                                                >
                                                    <AiFillDelete className="text-lg" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                                        {loading ? "Loading contents..." : "No contents found"}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {filteredContents.length > itemsPerPage && (
                <div className="flex justify-center mt-6">
                    <Pagination
                        current={currentPage}
                        total={filteredContents.length}
                        pageSize={itemsPerPage}
                        onChange={handlePageChange}
                        showSizeChanger={false}
                        showQuickJumper
                        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} contents`}
                    />
                </div>
            )}

            {/* Edit Content Modal */}
            {showEditContent && (
                <Modal
                    title="Edit Content"
                    open={showEditContent}
                    onCancel={() => {
                        setShowEditContent(false)
                    }}
                    footer={[
                        <button
                            key="cancel"
                            onClick={() => {
                                setShowEditContent(false)
                            }}
                            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 mr-2"
                        >
                            Cancel
                        </button>,
                        <button
                            key="submit"
                            onClick={() => handleFormSubmit(showEditContent)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                        >
                            {showEditContent ? "Update" : "Create"}
                        </button>
                    ]}
                    width={800}
                >
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) => setFormData({...formData, title: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter content title"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                            <Select
                                value={formData.type}
                                onChange={(value) => setFormData({...formData, type: value})}
                                className="w-full"
                            >
                                <Option value="privacy-policy">Privacy Policy</Option>
                                <Option value="terms-conditions">Terms & Conditions</Option>
                                <Option value="about-us">About Us</Option>
                                <Option value="faq">FAQ</Option>
                                <Option value="other">Other</Option>
                            </Select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                            <textarea
                                value={formData.content}
                                onChange={(e) => setFormData({...formData, content: e.target.value})}
                                rows={10}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter content"
                            />
                        </div>
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="isPublished"
                                checked={formData.isPublished}
                                onChange={(e) => setFormData({...formData, isPublished: e.target.checked})}
                                className="mr-2"
                            />
                            <label htmlFor="isPublished" className="text-sm font-medium text-gray-700">
                                Publish immediately
                            </label>
                        </div>
                    </div>
                </Modal>
            )}

            {/* View Content Modal */}
            {showViewContent && (
                <Modal
                    title="View Content"
                    open={showViewContent}
                    onCancel={() => setShowViewContent(false)}
                    footer={[
                        <button
                            key="close"
                            onClick={() => setShowViewContent(false)}
                            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                        >
                            Close
                        </button>
                    ]}
                    width={800}
                >
                    <div className="space-y-4">
                        <div>
                            <h3 className="text-lg font-medium text-gray-900">{selectedContent.title}</h3>
                            <p className="text-sm text-gray-500">Type: {selectedContent.type}</p>
                            <p className="text-sm text-gray-500">
                                Status: {selectedContent.isPublished ? 'Published' : 'Draft'}
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
                            <div className="border border-gray-300 rounded-md p-4 max-h-96 overflow-y-auto bg-gray-50">
                                <div dangerouslySetInnerHTML={{ __html: selectedContent.content }} />
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {loading && <Load />}
        </div>
    )
}

export default AdminContent
